import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { Lock } from "lucide-react";

/* ===================================================== */
/* SERVICES                                              */
/* ===================================================== */

import projectService from "../../services/projectService";
import taskService from "../../services/taskService";
import milestoneService from "../../services/milestoneService";
import companyService from "../../services/companyService";
/* ===================================================== */
/* AUTH                                                  */
/* ===================================================== */

import { useAuth } from "../../context/AuthContext";

/* ===================================================== */
/* TABS                                                  */
/* ===================================================== */

import ProjectHeader from "./ProjectHeader";
import OverviewTab from "./OverviewTab";
import TasksTab from "./TasksTab";
import MilestonesTab from "./MilestonesTab";
import PhasesTab from "./PhasesTab";

/* ===================================================== */
/* BILLING                                               */
/* ===================================================== */

import UpgradeToProModal from "../../components/billing/UpgradeToProModal";
import useUpgradeToPro from "../../hooks/useUpgradeToPro";

/* ===================================================== */
/* TASK NORMALIZER                                       */
/* ===================================================== */

const normalizeTask = (task) => {
  if (!task) return null;

  const t =
    task._id ? task : task.task;

  if (!t?._id) return null;

  return {
    ...t,

    _id: String(t._id),

    phaseId:
      typeof t.phaseId === "string"
        ? t.phaseId
        : t.phaseId?._id
        ? String(t.phaseId._id)
        : null,
  };
};

/* ===================================================== */
/* UPGRADE BLOCK                                         */
/* ===================================================== */

function UpgradeBlock({
  feature,
  onUpgrade,
}) {
  return (
    <div className="p-6 border rounded-xl bg-yellow-50 text-yellow-800 space-y-3">

      <div className="flex items-center gap-2 font-semibold">
        <Lock className="w-4 h-4" />
        {feature} are available on Pro plan
      </div>

      <p className="text-sm">
        Upgrade to unlock structured workflows and advanced collaboration.
      </p>

      <button
        onClick={onUpgrade}
        className="mt-2 px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800"
      >
        Upgrade to Pro
      </button>
    </div>
  );
}

/* ===================================================== */
/* PROJECT DETAILS                                       */
/* ===================================================== */

export default function ProjectDetails() {

  const { id: projectId } =
    useParams();

  const navigate =
    useNavigate();

  const { activeCompany } =
    useAuth();

  /* ===================================================== */
  /* PLAN                                                  */
  /* ===================================================== */

  const plan =
    activeCompany?.plan || "free";

  const isPro =
    ["pro", "enterprise"].includes(
      plan
    );

  /* ===================================================== */
  /* UPGRADE                                               */
  /* ===================================================== */

  const upgrade =
    useUpgradeToPro(activeCompany);

  /* ===================================================== */
  /* STATE                                                 */
  /* ===================================================== */

  const [project, setProject] =
    useState(null);

  const [tasks, setTasks] =
    useState([]);

  const [users, setUsers] =
    useState([]);

  const [milestones, setMilestones] =
    useState([]);

  const [tab, setTab] =
    useState("overview");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  /* ===================================================== */
  /* USERS LOADER                                          */
  /* ===================================================== */

  /* ===================================================== */
  /* USERS LOADER                                          */
  /* ===================================================== */

  const resolveUsers =
    useCallback(async (projectData) => {

      /**
       * Explicit project members
       */

      if (
        Array.isArray(
          projectData?.teamMembers
        ) &&
        projectData.teamMembers.length > 0
      ) {
        return projectData.teamMembers;
      }

      /**
       * Nested team members
       */

      if (
        Array.isArray(
          projectData?.team?.members
        ) &&
        projectData.team.members.length > 0
      ) {
        return projectData.team.members;
      }

      /**
       * Fallback → company users
       */

      const companyUsers =
        await companyService.getCompanyUsers();

      return Array.isArray(companyUsers)
        ? companyUsers
        : [];

    }, []);

  /* ===================================================== */
  /* LOAD PROJECT DATA                                     */
  /* ===================================================== */

  const loadProjectData =
    useCallback(async () => {

      if (!projectId) return;

      try {
        setLoading(true);
        setError("");

        /* -------------------------------------------------- */
        /* PROJECT                                             */
        /* -------------------------------------------------- */

        const projectData =
          await projectService.getProject(
            projectId
          );

        setProject(projectData);

        /* -------------------------------------------------- */
        /* USERS                                               */
        /* -------------------------------------------------- */

        try {
          const resolvedUsers =
            await resolveUsers(
              projectData
            );

          setUsers(
            resolvedUsers.filter(
              (u) => u && u._id
            )
          );

        } catch (err) {
          console.warn(
            "[Users] load failed:",
            err.message
          );

          setUsers([]);
        }

        /* -------------------------------------------------- */
        /* TASKS                                               */
        /* -------------------------------------------------- */

        try {
          const rawTasks =
            await taskService.getTasks(
              projectId
            );

          const normalizedTasks =
            Array.isArray(rawTasks)
              ? rawTasks
                  .map(normalizeTask)
                  .filter(Boolean)
              : [];

          setTasks(normalizedTasks);

        } catch (err) {
          console.warn(
            "[Tasks] load failed:",
            err.message
          );

          setTasks([]);
        }

        /* -------------------------------------------------- */
        /* MILESTONES                                          */
        /* -------------------------------------------------- */

        if (isPro) {
          try {
            const data =
              await milestoneService.listMilestones(
                projectId
              );

            setMilestones(
              Array.isArray(data)
                ? data
                : []
            );

          } catch (err) {
            console.warn(
              "[Milestones] load failed:",
              err.message
            );

            setMilestones([]);
          }
        }

      } catch (err) {

        console.error(
          "[ProjectDetails] load error:",
          err
        );

        setError(
          err?.message ||
            "Failed to load project"
        );

      } finally {

        setLoading(false);
      }

    }, [
      projectId,
      isPro,
      resolveUsers,
    ]);

  /* ===================================================== */
  /* EFFECT                                                */
  /* ===================================================== */

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  /* ===================================================== */
  /* LOADING                                               */
  /* ===================================================== */

  if (loading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Loading project...
      </div>
    );
  }

  /* ===================================================== */
  /* ERROR                                                 */
  /* ===================================================== */

  if (error) {
    return (
      <div className="p-6 space-y-3">

        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
          {error}
        </div>

        <button
          onClick={() =>
            navigate("/projects")
          }
          className="text-sm text-blue-600"
        >
          ← Back to projects
        </button>

      </div>
    );
  }

  /* ===================================================== */
  /* EMPTY                                                 */
  /* ===================================================== */

  if (!project) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Project not found
      </div>
    );
  }

  /* ===================================================== */
  /* TABS                                                  */
  /* ===================================================== */

  const tabs = [
    {
      key: "overview",
      label: "Overview",
    },

    {
      key: "tasks",
      label: "Tasks",
    },

    {
      key: "phases",
      label: "Phases",
      locked: !isPro,
    },

    {
      key: "milestones",
      label: "Milestones",
      locked: !isPro,
    },
  ];

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6">

      <ProjectHeader
        project={project}
        onBack={() =>
          navigate("/projects")
        }
      />

      {/* ===================================================== */}
      {/* TABS                                                  */}
      {/* ===================================================== */}

      <div className="flex gap-2 border-b pb-2 overflow-x-auto">

        {tabs.map((t) => (

          <button
            key={t.key}

            onClick={() => {

              if (t.locked) {
                upgrade.openModal();
                return;
              }

              setTab(t.key);
            }}

            className={`flex items-center gap-1 px-3 py-2 text-sm rounded transition ${
              tab === t.key
                ? "bg-black text-white"
                : "border text-gray-700 hover:bg-gray-50"
            } ${
              t.locked
                ? "opacity-80"
                : ""
            }`}
          >

            {t.label}

            {t.locked && (
              <Lock className="w-3.5 h-3.5 opacity-70" />
            )}

          </button>

        ))}

      </div>

      {/* ===================================================== */}
      {/* OVERVIEW                                              */}
      {/* ===================================================== */}

      {tab === "overview" && (
        <OverviewTab
          project={project}
          tasks={tasks}
        />
      )}

      {/* ===================================================== */}
      {/* TASKS                                                 */}
      {/* ===================================================== */}

      {tab === "tasks" && (
        <TasksTab
          projectId={projectId}
          tasks={tasks}
          setTasks={setTasks}
          phases={
            isPro
              ? project?.phases || []
              : []
          }
          users={users}
          isPro={isPro}
        />
      )}

      {/* ===================================================== */}
      {/* PHASES                                                */}
      {/* ===================================================== */}

      {tab === "phases" &&
        isPro && (
          <PhasesTab
            project={project}
            setProject={setProject}
            isPro={isPro}
          />
      )}

      {/* ===================================================== */}
      {/* MILESTONES                                            */}
      {/* ===================================================== */}

      {tab === "milestones" &&
        isPro && (
          <MilestonesTab
            projectId={projectId}
            milestones={milestones}
            setMilestones={
              setMilestones
            }
          />
      )}

      {/* ===================================================== */}
      {/* LOCKED STATES                                         */}
      {/* ===================================================== */}

      {tab === "phases" &&
        !isPro && (
          <UpgradeBlock
            feature="Phases"
            onUpgrade={() =>
              upgrade.openModal()
            }
          />
      )}

      {tab === "milestones" &&
        !isPro && (
          <UpgradeBlock
            feature="Milestones"
            onUpgrade={() =>
              upgrade.openModal()
            }
          />
      )}

      {/* ===================================================== */}
      {/* MODAL                                                 */}
      {/* ===================================================== */}

      <UpgradeToProModal
        open={upgrade.open}
        onClose={
          upgrade.closeModal
        }
        onConfirm={
          upgrade.startUpgrade
        }
        status={upgrade.status}
        loading={upgrade.loading}
        error={upgrade.error}
      />

    </div>
  );
}